const uuid = require('uuid')
const { normalizeProgram } = require('../tools/normalize-program')
const { addDays, format } = require('date-fns')
const RawExternalDataPGAdapter = require('../../common/raw-external-pg-adapter')
const DataOrderAdapter = require('../../order/data-access/data-order-pg-adapter')
const DataOrderLineAdapter = require('../../order/data-access/data-orderline-pg-adapter')
const SupplyPlanPgAdapter = require('../../supply-plan/data-access/supply-plan-pg-adapter')
const { WAREHOUSE_DEFINITIONS } = require('../../order/tools/read/warehouse-code-to-virtual')

const DEFAULT_LEAD_TIME_DAYS = 120

class PurchaseOrdersSyncAdapter {
  constructor ({ pgConnection, logger, user }) {
    this.pgConnection = pgConnection
    this.logger = logger
    this.user = user

    this.rawOrdersAdapter = new RawExternalDataPGAdapter({
      pgConnection,
      username: user,
      logger,
      tableName: 'raw.external_orders',
      extraColumns: ['source_event_id']
    })

    this.orderAdapter = new DataOrderAdapter(pgConnection, user, logger)
    this.orderLineAdapter = new DataOrderLineAdapter(pgConnection, user, logger)
    this.supplyPlanParamsAdapter = new SupplyPlanPgAdapter(pgConnection, user, logger)
  }

  async getLocationTypes (fsids) {
    if (!fsids.length) return {}
    const { rows } = await this.pgConnection.query(
      `SELECT fsid, supply_chain_level
       FROM avocado.data_location
       WHERE fsid = ANY($1)`,
      [fsids]
    )
    return Object.fromEntries(rows.map(r => [r.fsid, r.supply_chain_level]))
  }

  /**
   * Upsert raw external PO line items into raw.external_orders.
   */
  async syncRawOrders (organisationId, purchaseOrdersData, options = {}) {
    const sourceEventId = uuid.v4()

    // Last occurrence wins if SAP returns duplicate poNumber + Item combinations
    const seen = new Map()
    for (const item of purchaseOrdersData.items) {
      seen.set(`${item.poNumber}|${item.headerFields && item.headerFields['Item']}`, item)
    }

    const dataToStore = [...seen.values()].map(item => ({
      organisation_id: organisationId,
      source_event_id: sourceEventId,
      data: item
    }))

    const savedRecords = await this.rawOrdersAdapter.upsertBatchWithJsonbPaths(dataToStore, {
      conflictIdColumns: ['organisation_id'],
      conflictJsonbPaths: [
        { column: 'data', key: 'poNumber' },
        { column: 'data', path: ['headerFields', 'Item'] }
      ],
      batchSize: 2000,
      client: options.client
    })

    return { total: savedRecords.length, sourceEventId }
  }

  /**
   * Read from raw.external_orders and transform into
   * data_order / data_orderline shaped records.
   */
  async transformRawOrders (organisationId, options = {}) {
    const { sourceEventId } = options
    const filters = sourceEventId ? { source_event_id: sourceEventId } : {}
    const rawRecords = await this.rawOrdersAdapter.getByOrganisation(organisationId, null, { ...options, filters })

    const orders = []
    const linesByPoNumber = new Map()

    // Group line items by PO number — each raw record is one PO line
    for (const record of rawRecords) {
      const item = record.data
      const poNumber = item.poNumber
      const headerFields = item.headerFields || {}

      // Item "0" is a SAP header/summary row, not a real line item — skip it
      if (String(headerFields['Item'] || '').trim() === '0') continue

      if (!linesByPoNumber.has(poNumber)) {
        const deletionIndicator = headerFields['Deletion Indicator']
        const wbsElement = headerFields.wbsElement || headerFields['WBS Element']
        const normalizedProgram = normalizeProgram(wbsElement)
        const programPrefix = normalizedProgram && normalizedProgram.toLowerCase()
        const programId = programPrefix && `program:${programPrefix}`
        const funderId = programPrefix && `funder:${programPrefix}`
        const warehouseDef = WAREHOUSE_DEFINITIONS.RMS[item.plant]
        const destinationId = warehouseDef && warehouseDef._id

        // Skip POs with no warehouse mapping or no WBS element
        if (!destinationId || !programId) {
          linesByPoNumber.set(poNumber, { lines: [], docDate: item.docDate, deliveryDate: headerFields['Delivery Date'] || null, programId, deletionIndicator, skip: true })
          continue
        }

        linesByPoNumber.set(poNumber, { lines: [], docDate: item.docDate, deliveryDate: headerFields['Delivery Date'] || null, programId, deletionIndicator })

        orders.push({
          id: uuid.v4(),
          order_id: poNumber,
          sub_order_id: poNumber,
          group_id: poNumber,
          order_type: 'external',
          status: deletionIndicator === 'L' ? 'cancelled' : 'accepted',
          destination_id: destinationId,
          program_id: programId,
          funder_id: funderId,
          is_complete: false, // computed after all lines are collected
          delivery_date: null
        })
      }

      const original = parseFloat(headerFields['Order Quantity'] || 0)
      const stillToDeliver = parseFloat(headerFields['Still to be delivered (qty)'] || 0)

      const sku = headerFields.Material
      const orderedQty = Math.round(original)
      const receivedQty = Math.max(0, Math.round(original - stillToDeliver))

      const poEntry = linesByPoNumber.get(poNumber)
      if (!poEntry || poEntry.skip) continue

      const existingLine = poEntry.lines.find(l => l.sku === sku)
      if (existingLine) {
        existingLine.original += orderedQty
        existingLine.adjusted += orderedQty
        existingLine.received += receivedQty
      } else {
        poEntry.lines.push({
          sku,
          original: orderedQty,
          adjusted: orderedQty,
          received: receivedQty
        })
      }
    }

    // Compute is_complete for each order based on all its lines
    for (const order of orders) {
      const { lines, deletionIndicator } = linesByPoNumber.get(order.order_id)
      order.is_complete = deletionIndicator === 'L' ||
        (lines.length > 0 && lines.every(line => line.original > 0 && line.received >= line.original))
    }

    // Batch fetch location types for all unique destination fsids upfront
    const uniqueDestinations = [...new Set(orders.map(o => o.destination_id).filter(Boolean))]
    const locationTypeByFsid = await this.getLocationTypes(uniqueDestinations)

    // Build supply plan lookup params
    const supplyPlanLookups = orders.reduce((acc, order) => {
      const { lines, docDate, programId } = linesByPoNumber.get(order.order_id)
      const sku = lines[0] && lines[0].sku
      const locationType = locationTypeByFsid[order.destination_id]
      if (sku && programId && locationType) {
        acc.push({ programFsid: programId, sku, locationType, date: docDate })
      }
      return acc
    }, [])

    const leadTimeByKey = await this.supplyPlanParamsAdapter.getLatestBatch(supplyPlanLookups)

    // Compute delivery_date for each order
    for (const order of orders) {
      const { lines, docDate, deliveryDate, programId } = linesByPoNumber.get(order.order_id)
      const sku = lines[0] && lines[0].sku
      const locationType = locationTypeByFsid[order.destination_id]
      const leadTimeDays = leadTimeByKey[`${programId}|${sku}|${locationType}`] || DEFAULT_LEAD_TIME_DAYS
      order.delivery_date = deliveryDate || format(addDays(new Date(docDate), leadTimeDays), 'YYYY-MM-DD')
    }

    return { orders, linesByPoNumber }
  }

  /**
   * Upsert transformed orders and lines into data_order / data_orderline.
   */
  async syncTransformedOrders (organisationId, options = {}) {
    const { orders, linesByPoNumber } = await this.transformRawOrders(organisationId, options)

    if (orders.length === 0) {
      this.logger.info('No external orders to sync', { organisationId })
      return
    }

    // Batch upsert all orders
    const savedOrders = await this.orderAdapter.bulkUpsert(orders, {
      conflictIdColumns: ['order_id'],
      excludeOnUpdate: ['id'],
      batchSize: 2000,
      client: options.client
    })

    // Map PO number → internal data_order.id for FK on order lines
    const idByPoNumber = Object.fromEntries(
      savedOrders.map(r => [r.order_id, r.id])
    )

    const orderLines = orders.flatMap(order => {
      const { lines } = linesByPoNumber.get(order.order_id)
      const internalId = idByPoNumber[order.order_id]
      return lines.map(line => ({
        order_id: internalId,
        sku: line.sku,
        original: line.original,
        adjusted: line.adjusted,
        received: line.received
      }))
    })

    // Batch upsert all lines
    await this.orderLineAdapter.bulkUpsert(orderLines, {
      conflictIdColumns: ['order_id', 'sku'],
      batchSize: 2000,
      client: options.client
    })

    this.logger.info('Successfully synced external orders', {
      organisationId,
      totalOrders: orders.length,
      totalLines: orderLines.length
    })
  }
}

module.exports = PurchaseOrdersSyncAdapter
